import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslate } from '../hooks/useTranslate';
import type { SchemaComparisonResponse, SchemaValidationStatus } from '../types';

interface SchemaComparisonModalProps {
    isOpen: boolean;
    onClose: () => void;
    onProceed: () => void;
    onSkipToPreview?: () => void;
    result: SchemaComparisonResponse | null;
    loading: boolean;
    error: string | null;
    sourceType: 'file' | 'table';
    dataObjectName: string;
    onRetry?: () => void;
}

function deriveStatus(result: SchemaComparisonResponse | null): SchemaValidationStatus | null {
    if (!result) return null;
    if (result.is_completely_different) return 'completely_different';
    if (!result.is_compatible) return 'incompatible';
    return 'compatible';
}

/* ---------- column entry helpers ---------- */

type ColumnStatus = 'matched' | 'missing' | 'extra';
type TabKey = 'all' | ColumnStatus;

interface ColumnEntry {
    name: string;
    type: string | null;
    status: ColumnStatus;
}

function buildColumnEntries(result: SchemaComparisonResponse): ColumnEntry[] {
    const matched: ColumnEntry[] = result.matched_columns.map(name => ({ name, type: null, status: 'matched' }));
    const missing: ColumnEntry[] = result.missing_columns.map(col => ({ name: col.name, type: col.data_type, status: 'missing' }));
    const extra: ColumnEntry[] = result.extra_columns.map(name => ({ name, type: null, status: 'extra' }));
    return [...missing, ...extra, ...matched];
}

const statusBadgeConfig: Record<ColumnStatus, { label: string; className: string; icon: React.ReactNode }> = {
    matched: {
        label: 'Matched',
        className: 'bg-green-50 text-green-700 border-green-200',
        icon: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>,
    },
    missing: {
        label: 'Missing',
        className: 'bg-red-50 text-red-700 border-red-200',
        icon: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>,
    },
    extra: {
        label: 'New',
        className: 'bg-blue-50 text-blue-700 border-blue-200',
        icon: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>,
    },
};

/* ---------- main component ---------- */

const SchemaComparisonModal: React.FC<SchemaComparisonModalProps> = ({
    isOpen,
    onClose,
    onProceed,
    onSkipToPreview,
    result,
    loading,
    error,
    sourceType,
    dataObjectName,
    onRetry,
}) => {
    const { t } = useTranslate();
    const dialogRef = useRef<HTMLDivElement>(null);

    /* ---- tab + search state for column table ---- */
    const [activeTab, setActiveTab] = useState<TabKey>('all');
    const [searchQuery, setSearchQuery] = useState('');

    const allColumns = useMemo(() => (result ? buildColumnEntries(result) : []), [result]);

    const filteredColumns = useMemo(() => {
        let cols = activeTab === 'all' ? allColumns : allColumns.filter(c => c.status === activeTab);
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            cols = cols.filter(c => c.name.toLowerCase().includes(q));
        }
        return cols;
    }, [allColumns, activeTab, searchQuery]);

    const tabCounts = useMemo(() => ({
        all: allColumns.length,
        matched: result?.matched_columns.length ?? 0,
        missing: result?.missing_columns.length ?? 0,
        extra: result?.extra_columns.length ?? 0,
    }), [allColumns, result]);

    const handleTabChange = (tab: TabKey) => {
        setActiveTab(tab);
        setSearchQuery('');
    };

    // Focus trap — focus dialog on open
    useEffect(() => {
        if (isOpen) {
            dialogRef.current?.focus();
            document.body.style.overflow = 'hidden';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    if (!isOpen) return null;

    const status = deriveStatus(result);
    const canProceed = status === 'compatible';

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
        }
    };

    /* ---- status banner config ---- */
    const bannerConfig: Record<SchemaValidationStatus, { bg: string; icon: React.ReactNode; title: string }> = {
        compatible: {
            bg: 'bg-green-50 border-green-200',
            icon: (
                <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            ),
            title: t('Schema is Compatible'),
        },
        incompatible: {
            bg: 'bg-yellow-50 border-yellow-300',
            icon: (
                <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            ),
            title: t('Missing Required Columns'),
        },
        completely_different: {
            bg: 'bg-red-50 border-red-300',
            icon: (
                <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            ),
            title: t('Completely Different Schema'),
        },
    };

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="schema-comparison-title"
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-auto"
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            ref={dialogRef}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-gray-900/50 to-black/70 backdrop-blur-md" />

            {/* Modal */}
            <div
                className="relative rounded-3xl border border-gray-200/50 shadow-2xl overflow-hidden flex flex-col"
                style={{
                    width: 'clamp(420px, 55vw, 720px)',
                    maxHeight: '80vh',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)',
                    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                }}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 px-8 py-5 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-11 h-11 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
                                </svg>
                            </div>
                            <div>
                                <h2 id="schema-comparison-title" className="text-xl font-bold text-white tracking-tight">
                                    {t('Schema Validation')}
                                </h2>
                                <p className="text-red-100 text-sm font-medium">
                                    {dataObjectName}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-white transition-all duration-200 hover:scale-105"
                            aria-label={t('Close')}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-8 py-6 min-h-0">
                    {/* Loading */}
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-16 gap-4">
                            <div className="loader-red loader-md" />
                            <span className="text-sm text-gray-500 font-medium">{t('Validating schema compatibility...')}</span>
                        </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                            <div className="flex items-start gap-3">
                                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-red-800">{t('Schema comparison failed')}</p>
                                    <p className="text-sm text-red-600 mt-1">{error}</p>
                                </div>
                            </div>
                            {onRetry && (
                                <button
                                    onClick={onRetry}
                                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-700 bg-white border border-red-200 rounded-xl hover:bg-red-50 transition-all duration-200"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                    {t('Retry')}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Result */}
                    {!loading && !error && result && status && (
                        <>
                            {/* Status banner */}
                            <div className={`border rounded-xl p-4 mb-5 ${bannerConfig[status].bg}`}>
                                <div className="flex items-start gap-3">
                                    {bannerConfig[status].icon}
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-gray-900">{bannerConfig[status].title}</p>
                                        <p className="text-sm text-gray-600 mt-1">{result.summary}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Tab bar */}
                            <div className="flex items-center gap-1 mb-3 border-b border-gray-200" role="tablist" aria-label={t('Column status filter')}>
                                {([
                                    { key: 'all' as TabKey, label: t('All') },
                                    { key: 'matched' as TabKey, label: t('Matched') },
                                    { key: 'missing' as TabKey, label: t('Missing') },
                                    { key: 'extra' as TabKey, label: t('New') },
                                ] as const).map(tab => {
                                    const isActive = activeTab === tab.key;
                                    const count = tabCounts[tab.key];
                                    const colorMap: Record<TabKey, string> = {
                                        all: 'border-red-600 text-red-700',
                                        matched: 'border-green-600 text-green-700',
                                        missing: 'border-red-600 text-red-700',
                                        extra: 'border-blue-600 text-blue-700',
                                    };
                                    return (
                                        <button
                                            key={tab.key}
                                            role="tab"
                                            aria-selected={isActive}
                                            onClick={() => handleTabChange(tab.key)}
                                            className={`px-3 py-2 text-xs font-semibold border-b-2 transition-colors -mb-px ${
                                                isActive
                                                    ? colorMap[tab.key]
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                        >
                                            {tab.label}
                                            <span className={`ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                                                isActive ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                                {count}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Search bar */}
                            <div className="relative mb-3">
                                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder={t('Search columns...')}
                                    aria-label={t('Search columns...')}
                                    className="w-full pl-9 pr-8 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 transition-all placeholder:text-gray-400"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        aria-label={t('Clear search')}
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                )}
                            </div>

                            {/* Column table */}
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="max-h-[300px] overflow-y-auto">
                                    <table className="w-full text-xs table-fixed">
                                        <caption className="sr-only">{t('Schema comparison column details')}</caption>
                                        <thead className="bg-brand-700 sticky top-0 z-10">
                                            <tr>
                                                <th scope="col" className="w-[40px] px-3 py-2.5 text-left text-xs font-bold text-white border-b border-brand-600 border-r border-brand-600">#</th>
                                                <th scope="col" style={{ width: '65%' }} className="px-3 py-2.5 text-left text-xs font-bold text-white border-b border-brand-600 border-r border-brand-600">{t('Column Name')}</th>
                                                <th scope="col" style={{ width: '30%' }} className="px-3 py-2.5 text-left text-xs font-bold text-white border-b border-brand-600">{t('Status')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-brand-100">
                                            {filteredColumns.length === 0 ? (
                                                <tr>
                                                    <td colSpan={3} className="px-3 py-8 text-center text-gray-400 font-medium">
                                                        {searchQuery
                                                            ? `${t('No columns matching')} "${searchQuery}"`
                                                            : activeTab === 'matched' ? t('No matched columns')
                                                            : activeTab === 'missing' ? t('No missing columns')
                                                            : activeTab === 'extra' ? t('No extra columns')
                                                            : t('No columns')}
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredColumns.map((col, idx) => {
                                                    const badge = statusBadgeConfig[col.status];
                                                    return (
                                                        <tr key={`${col.status}-${col.name}`} className="hover:bg-brand-50 transition-colors">
                                                            <td className="w-[40px] px-3 py-2 text-gray-400 font-mono border-r border-gray-100">{idx + 1}</td>
                                                            <td className="px-3 py-2 font-medium text-gray-900 truncate border-r border-gray-100" title={col.name}>{col.name}</td>
                                                            <td className="px-3 py-2">
                                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${badge.className}`}>
                                                                    {badge.icon}
                                                                    {t(badge.label)}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Informational note for extra columns */}
                            {result.extra_columns.length > 0 && status === 'compatible' && (
                                <p className="text-xs text-gray-500 mt-3">
                                    {t('Extra columns will be available for configuration after proceeding.')}
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-8 py-4 bg-gradient-to-r from-red-50 to-pink-50 border-t border-red-100 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-gray-800 transition-all duration-200"
                    >
                        {t('Go Back')}
                    </button>

                    {!error && (
                        <div className="flex items-center gap-3">
                            {/* Skip to Preview — available for file uploads when compatible */}
                            {onSkipToPreview && canProceed && !loading && (
                                <button
                                    onClick={onSkipToPreview}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-gray-800 transition-all duration-200"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                                    {t('Skip to Preview')}
                                </button>
                            )}

                            {/* Primary action button */}
                            <button
                                onClick={onProceed}
                                disabled={!canProceed || loading}
                                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white rounded-xl shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 hover:scale-105"
                                style={{
                                    background: canProceed
                                        ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                                        : '#9ca3af',
                                    boxShadow: canProceed
                                        ? '0 4px 15px rgba(220, 38, 38, 0.3)'
                                        : 'none',
                                }}
                                title={!canProceed ? t('Cannot proceed — required columns are missing') : undefined}
                            >
                                {loading ? (
                                    <>
                                        <div className="loader-red loader-sm" style={{ width: 16, height: 16, borderWidth: 2 }} />
                                        {t('Validating...')}
                                    </>
                                ) : (
                                    <>
                                        {canProceed && (
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        )}
                                        {sourceType === 'file' ? t('Update Metadata') : t('Proceed with Re-trigger')}
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SchemaComparisonModal;
